import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Eye, Loader2, Pencil, Trash2, Palette, Plus } from "lucide-react";

import { BottomSheet } from "@/components/ui/bottom-sheet";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import { useIsMobile } from "@/hooks/useResponsive";
import { useToast } from "@/hooks/use-toast";
import { useThemeEditor } from "@/hooks/use-theme-editor";
import { themesApi } from "@/lib/api/themes";
import { cn } from "@/lib/utils";
import { previewMessage } from "@/lib/visual-editor/post-message";
import { Theme } from "@/types/theme.types";

import { BulkDeleteConfirmDialog } from "./components/bulk-delete-confirm-dialog";
import { ThemeCardWithSelection } from "./components/theme-card-with-selection";
import { ThemePreviewIframe } from "./components/theme-preview-iframe";
import { ThemesListPanel } from "./themes-list-panel";

interface ThemeLibrarySettingsProps {
  workspaceId: string;
}

type FilterType = "all" | "built-in" | "custom";

export function ThemeLibrarySettings({
  workspaceId,
}: ThemeLibrarySettingsProps): JSX.Element {
  const { toast } = useToast();
  const { getIdToken } = useAuth();
  const { isAdminOrOwner } = usePermissions();
  const isMobile = useIsMobile();

  // State management
  const [selectedThemeIds, setSelectedThemeIds] = useState<string[]>([]);
  const [filterType, setFilterType] = useState<FilterType>("all");
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTheme, setEditingTheme] = useState<Theme | null>(null);
  const [previewIframe, setPreviewIframe] = useState<HTMLIFrameElement | null>(
    null,
  );
  const [editPreviewIframe, setEditPreviewIframe] =
    useState<HTMLIFrameElement | null>(null);

  // Theme editor hook
  const { themes, deleteTheme, loadThemes, isLoading } = useThemeEditor({
    workspaceId,
    previewIframe: null, // No preview needed in settings view
  });

  // Load themes on mount
  React.useEffect(() => {
    loadThemes();
  }, [loadThemes]);

  // Memoized filtered themes
  const filteredThemes = useMemo((): Theme[] => {
    if (filterType === "all") return themes;
    return themes.filter((t) => t.type === filterType);
  }, [themes, filterType]);

  // Memoized counts
  const builtInCount = useMemo(
    () => themes.filter((t) => t.type === "built-in").length,
    [themes],
  );
  const customCount = useMemo(
    () => themes.filter((t) => t.type === "custom").length,
    [themes],
  );

  // Memoized visible custom themes
  const visibleCustomThemes = useMemo(
    () => filteredThemes.filter((t) => t.type === "custom"),
    [filteredThemes],
  );

  // Filter selected IDs to only include custom themes
  const selectedCustomThemeIds = useMemo(
    () =>
      selectedThemeIds.filter((id) => {
        const theme = themes.find((t) => t.themeId === id);
        return theme?.type === "custom";
      }),
    [selectedThemeIds, themes],
  );

  // Get selected themes for dialog
  const selectedThemesForDialog = useMemo(
    () => themes.filter((t) => selectedCustomThemeIds.includes(t.themeId)),
    [themes, selectedCustomThemeIds],
  );

  // Check if all visible custom themes are selected
  const allVisibleSelected = useMemo(() => {
    if (visibleCustomThemes.length === 0) return false;
    return visibleCustomThemes.every((t) =>
      selectedThemeIds.includes(t.themeId),
    );
  }, [visibleCustomThemes, selectedThemeIds]);

  // Selection handlers
  const toggleThemeSelection = useCallback((themeId: string): void => {
    setSelectedThemeIds((prev) =>
      prev.includes(themeId)
        ? prev.filter((id) => id !== themeId)
        : [...prev, themeId],
    );
  }, []);

  const toggleSelectAll = useCallback((): void => {
    const visibleCustomIds = visibleCustomThemes.map((t) => t.themeId);
    const allSelected = visibleCustomIds.every((id) =>
      selectedThemeIds.includes(id),
    );

    if (allSelected) {
      // Deselect all visible custom themes
      setSelectedThemeIds((prev) =>
        prev.filter((id) => !visibleCustomIds.includes(id)),
      );
    } else {
      // Select all visible custom themes
      setSelectedThemeIds((prev) => {
        const newIds = visibleCustomIds.filter((id) => !prev.includes(id));
        return [...prev, ...newIds];
      });
    }
  }, [visibleCustomThemes, selectedThemeIds]);

  const clearSelection = useCallback((): void => {
    setSelectedThemeIds([]);
  }, []);

  // Bulk delete handler — single API call
  const handleBulkDelete = useCallback(async (): Promise<void> => {
    setIsDeleting(true);

    try {
      const token = await getIdToken();
      if (!token) return;

      // Map themeIds to database UUIDs
      const ids = selectedCustomThemeIds
        .map((themeId) => themes.find((t) => t.themeId === themeId)?.id)
        .filter((id): id is string => !!id);

      if (ids.length === 0) return;

      const { deletedCount } = await themesApi.bulkDeleteThemes(token, ids);

      if (deletedCount > 0) {
        toast({
          title: `${deletedCount} theme${deletedCount > 1 ? "s" : ""} deleted`,
        });
        loadThemes(true);
      }

      clearSelection();
      setBulkDeleteDialogOpen(false);
    } catch {
      toast({ title: "Failed to delete themes", variant: "destructive" });
    } finally {
      setIsDeleting(false);
    }
  }, [
    selectedCustomThemeIds,
    themes,
    getIdToken,
    loadThemes,
    clearSelection,
    toast,
  ]);

  // Handler for when theme is created
  const handleThemeCreated = useCallback(() => {
    setCreateDialogOpen(false);
    loadThemes(true); // Silent reload — no loading spinner
  }, [loadThemes]);

  // Handler for editing a theme
  const handleEditTheme = useCallback((theme: Theme) => {
    setEditingTheme(theme);
    setEditDialogOpen(true);
  }, []);

  // Handler for when theme is updated
  const handleThemeUpdated = useCallback(() => {
    setEditDialogOpen(false);
    setEditingTheme(null);
    loadThemes(true); // Silent reload — no loading spinner
  }, [loadThemes]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Empty state check
  const isEmpty = themes.length === 0;

  return (
    <>
      {isEmpty ? (
        // Empty state - no themes at all
        <div className={cn("flex flex-col items-center justify-center h-full text-center", isMobile ? "py-8 px-6" : "py-12")}>
          <Palette className={cn("text-muted-foreground mb-4", isMobile ? "h-10 w-10" : "h-12 w-12")} />
          <h3 className={cn("font-medium mb-1", isMobile ? "text-base" : "text-lg")}>No themes available</h3>
          <p className={cn("text-muted-foreground mb-4", isMobile ? "text-xs" : "text-sm")}>
            {isAdminOrOwner
              ? "Get started by creating your first custom theme."
              : "No themes have been created yet."}
          </p>
          {isAdminOrOwner && (
            <Button variant="ghost" onClick={() => setCreateDialogOpen(true)} className={cn("text-xs h-8 px-3 bg-foreground/[0.07] hover:bg-foreground/[0.12] text-foreground/80 hover:text-foreground", isMobile && "min-h-[44px]")}>
              <Plus className="h-3.5 w-3.5 mr-1.5" />
              Create Theme
            </Button>
          )}
        </div>
      ) : (
        // Main view with themes
        <div className="flex flex-col h-full">
          {/* Toolbar: Filter tabs + Actions */}
          <div className={cn(isMobile ? "flex flex-col gap-3" : "flex items-center justify-between gap-3", "pb-4")}>
            <Tabs
              value={filterType}
              onValueChange={(v) => setFilterType(v as FilterType)}
              className={cn("flex-1", isMobile && "w-full")}
            >
              <TabsList className={cn(isMobile && "w-full")}>
                <TabsTrigger value="all" className={cn(isMobile && "flex-1 text-xs")}>All ({themes.length})</TabsTrigger>
                <TabsTrigger value="built-in" className={cn(isMobile && "flex-1 text-xs")}>
                  Built-in ({builtInCount})
                </TabsTrigger>
                <TabsTrigger value="custom" className={cn(isMobile && "flex-1 text-xs")}>Custom ({customCount})</TabsTrigger>
              </TabsList>
            </Tabs>

            <div className={cn("flex items-center gap-2", isMobile && "w-full justify-end")}>
              {/* Selection controls — only for custom themes visible to admins */}
              {isAdminOrOwner && visibleCustomThemes.length > 0 && (
                <>
                  <label
                    htmlFor="select-all-themes"
                    className="flex items-center gap-1.5 cursor-pointer select-none group"
                  >
                    <Checkbox
                      id="select-all-themes"
                      checked={allVisibleSelected}
                      onCheckedChange={toggleSelectAll}
                      className={cn(
                        "border-primary rounded-sm bg-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:border-primary data-[state=checked]:text-primary-foreground",
                        isMobile ? "h-5 w-5" : "h-4 w-4",
                      )}
                    />
                    <span className="text-xs text-muted-foreground group-hover:text-foreground transition-colors">
                      {selectedCustomThemeIds.length > 0
                        ? `${selectedCustomThemeIds.length} selected`
                        : "Select"}
                    </span>
                  </label>

                  {selectedCustomThemeIds.length > 0 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setBulkDeleteDialogOpen(true)}
                      className={cn(
                        "text-muted-foreground hover:text-destructive hover:bg-destructive/10",
                        isMobile ? "h-9 w-9" : "h-7 w-7",
                      )}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}

                  <div className="w-px h-4 bg-border" />
                </>
              )}

              {isAdminOrOwner && (
                <Button variant="ghost" onClick={() => setCreateDialogOpen(true)} className="text-xs h-8 px-3 bg-foreground/[0.07] hover:bg-foreground/[0.12] text-foreground/80 hover:text-foreground">
                  <Plus className="h-4 w-4 mr-1" />
                  Create
                </Button>
              )}
            </div>
          </div>

          {/* Theme grid */}
          <ScrollArea className="flex-1">
            {filteredThemes.length === 0 ? (
              <div className={cn("flex flex-col items-center justify-center text-center", isMobile ? "py-10 px-4" : "py-16")}>
                <Palette className={cn("text-muted-foreground mb-4", isMobile ? "h-10 w-10" : "h-12 w-12")} />
                <h3 className={cn("font-medium mb-1", isMobile ? "text-sm" : "text-base")}>
                  No {filterType === "all" ? "" : filterType} themes found
                </h3>
                <p className={cn("text-muted-foreground", isMobile ? "text-xs" : "text-sm")}>
                  {filterType === "custom"
                    ? "Create your first custom theme to get started."
                    : `There are no ${filterType} themes available.`}
                </p>
              </div>
            ) : (
              <div className={cn("grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 p-1", isMobile ? "gap-2" : "gap-3")}>
                {filteredThemes.map((theme) => (
                  <ThemeCardWithSelection
                    key={theme.id}
                    theme={theme}
                    isSelected={selectedThemeIds.includes(theme.themeId)}
                    onToggle={isAdminOrOwner ? toggleThemeSelection : undefined}
                    onEdit={isAdminOrOwner ? handleEditTheme : undefined}
                  />
                ))}
              </div>
            )}
          </ScrollArea>

          {/* Confirmation dialog */}
          <BulkDeleteConfirmDialog
            open={bulkDeleteDialogOpen}
            onOpenChange={setBulkDeleteDialogOpen}
            themes={selectedThemesForDialog}
            onConfirm={handleBulkDelete}
            isDeleting={isDeleting}
          />
        </div>
      )}

      {/* Create Theme Dialog - single instance */}
      <CreateThemeDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        workspaceId={workspaceId}
        previewIframe={previewIframe}
        onPreviewIframeReady={setPreviewIframe}
        onThemeCreated={handleThemeCreated}
      />

      {/* Edit Theme Dialog */}
      <EditThemeDialog
        open={editDialogOpen}
        onOpenChange={(open) => {
          setEditDialogOpen(open);
          if (!open) setEditingTheme(null);
        }}
        workspaceId={workspaceId}
        theme={editingTheme}
        previewIframe={editPreviewIframe}
        onPreviewIframeReady={setEditPreviewIframe}
        onThemeUpdated={handleThemeUpdated}
      />
    </>
  );
}

/**
 * CreateThemeDialog - Dialog for creating themes with live preview
 */
interface CreateThemeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  previewIframe: HTMLIFrameElement | null;
  onPreviewIframeReady: (iframe: HTMLIFrameElement) => void;
  onThemeCreated: () => void;
}

function CreateThemeDialog({
  open,
  onOpenChange,
  workspaceId,
  previewIframe,
  onPreviewIframeReady,
  onThemeCreated,
}: CreateThemeDialogProps): JSX.Element {
  const isMobile = useIsMobile();
  const [mobileView, setMobileView] = useState<"editor" | "preview">("editor");

  if (isMobile) {
    return (
      <BottomSheet open={open} onOpenChange={onOpenChange} title="Create Theme">
        <div className="flex flex-col h-full">
          {/* Toggle buttons */}
          <div className="flex gap-2 px-4 pb-2">
            <Button
              variant="ghost"
              className={cn("flex-1 text-xs h-8", mobileView === "editor" ? "bg-foreground/[0.07] text-foreground" : "text-muted-foreground")}
              onClick={() => setMobileView("editor")}
            >
              <Pencil className="h-3.5 w-3.5 mr-1.5" />
              Editor
            </Button>
            <Button
              variant="ghost"
              className={cn("flex-1 text-xs h-8", mobileView === "preview" ? "bg-foreground/[0.07] text-foreground" : "text-muted-foreground")}
              onClick={() => setMobileView("preview")}
            >
              <Eye className="h-3.5 w-3.5 mr-1.5" />
              Preview
            </Button>
          </div>

          {/* Content — both panels always mounted, visibility toggled */}
          <div className={cn("flex-1 overflow-hidden", mobileView !== "editor" && "hidden")}>
            <ThemesListPanel
              onBack={() => onOpenChange(false)}
              previewIframe={previewIframe}
              workspaceId={workspaceId}
              standaloneMode={true}
              onThemeSelect={onThemeCreated}
            />
          </div>
          <div className={cn("flex-1 overflow-hidden bg-muted/30", mobileView !== "preview" && "hidden")}>
            <ThemePreviewIframe
              onIframeReady={onPreviewIframeReady}
              className="w-full h-full"
            />
          </div>
        </div>
      </BottomSheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] lg:max-w-[1600px] h-[90vh] p-0 flex flex-col overflow-hidden">
        <DialogHeader className="px-4 py-3 border-b">
          <DialogTitle>Create Theme</DialogTitle>
        </DialogHeader>

        <div className="flex flex-1 min-h-0">
          {/* Left: Theme Editor */}
          <div className="w-[400px] flex-shrink-0 border-r border-border overflow-hidden">
            <ThemesListPanel
              onBack={() => onOpenChange(false)}
              previewIframe={previewIframe}
              workspaceId={workspaceId}
              standaloneMode={true}
              onThemeSelect={onThemeCreated}
            />
          </div>

          {/* Right: Live Preview */}
          <div className="flex-1 bg-muted/30">
            <ThemePreviewIframe
              onIframeReady={onPreviewIframeReady}
              className="w-full h-full"
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * EditThemeDialog - Dialog for editing existing themes with live preview
 */
interface EditThemeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  theme: Theme | null;
  previewIframe: HTMLIFrameElement | null;
  onPreviewIframeReady: (iframe: HTMLIFrameElement) => void;
  onThemeUpdated: () => void;
}

function EditThemeDialog({
  open,
  onOpenChange,
  workspaceId,
  theme,
  previewIframe,
  onPreviewIframeReady,
  onThemeUpdated,
}: EditThemeDialogProps): JSX.Element | null {
  const isMobile = useIsMobile();
  const [mobileView, setMobileView] = useState<"editor" | "preview">("editor");

  // Send initial theme preview when iframe is ready
  useEffect(() => {
    if (open && previewIframe && theme) {
      previewMessage.updateTheme(previewIframe, theme.themeId, theme.content);
    }
  }, [open, previewIframe, theme]);

  if (!theme) return null;

  if (isMobile) {
    return (
      <BottomSheet open={open} onOpenChange={onOpenChange} title={`Edit: ${theme.name}`}>
        <div className="flex flex-col h-full">
          {/* Toggle buttons */}
          <div className="flex gap-2 px-4 pb-2">
            <Button
              variant="ghost"
              className={cn("flex-1 text-xs h-8", mobileView === "editor" ? "bg-foreground/[0.07] text-foreground" : "text-muted-foreground")}
              onClick={() => setMobileView("editor")}
            >
              <Pencil className="h-3.5 w-3.5 mr-1.5" />
              Editor
            </Button>
            <Button
              variant="ghost"
              className={cn("flex-1 text-xs h-8", mobileView === "preview" ? "bg-foreground/[0.07] text-foreground" : "text-muted-foreground")}
              onClick={() => setMobileView("preview")}
            >
              <Eye className="h-3.5 w-3.5 mr-1.5" />
              Preview
            </Button>
          </div>

          {/* Content — both panels always mounted, visibility toggled */}
          <div className={cn("flex-1 overflow-hidden", mobileView !== "editor" && "hidden")}>
            <ThemesListPanel
              onBack={() => onOpenChange(false)}
              previewIframe={previewIframe}
              workspaceId={workspaceId}
              standaloneMode={true}
              initialThemeId={theme.themeId}
              onThemeSelect={onThemeUpdated}
            />
          </div>
          <div className={cn("flex-1 overflow-hidden bg-muted/30", mobileView !== "preview" && "hidden")}>
            <ThemePreviewIframe
              onIframeReady={onPreviewIframeReady}
              className="w-full h-full"
            />
          </div>
        </div>
      </BottomSheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] lg:max-w-[1600px] h-[90vh] p-0 flex flex-col overflow-hidden">
        <DialogHeader className="px-4 py-3 border-b">
          <DialogTitle>Edit Theme: {theme.name}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-1 min-h-0">
          {/* Left: Theme Editor */}
          <div className="w-[400px] flex-shrink-0 border-r border-border overflow-hidden">
            <ThemesListPanel
              onBack={() => onOpenChange(false)}
              previewIframe={previewIframe}
              workspaceId={workspaceId}
              standaloneMode={true}
              initialThemeId={theme.themeId}
              onThemeSelect={onThemeUpdated}
            />
          </div>

          {/* Right: Live Preview */}
          <div className="flex-1 bg-muted/30">
            <ThemePreviewIframe
              onIframeReady={onPreviewIframeReady}
              className="w-full h-full"
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
